import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, Tag, message, Button, Space, Modal, Form, Input, Select, Upload, Row, Col } from 'antd';
import { ProjectOutlined, DownloadOutlined, EditOutlined, UploadOutlined, PlusOutlined, SearchOutlined, EyeOutlined } from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

const SOWDocumentation = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const formatIndianDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
      timeZone: 'Asia/Kolkata',
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/sow-documents/`);
      const data = await response.json();
      if (data.success) {
        setDocuments(data.data);
      } else {
        message.error(data.message || 'Failed to fetch documents');
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      message.error('An error occurred while fetching SOW documents');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditingRecord(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setIsViewModalVisible(true);
  };

  const handleEdit = (record) => {
    setIsEditMode(true);
    setEditingRecord(record);

    let existingFileList = [];
    if (record.document) {
      const url = record.document.startsWith('http')
        ? record.document
        : `${import.meta.env.VITE_API_BASE_URL}/media/${record.document}`;

      existingFileList = [{
        uid: '-1',
        name: record.document_name || 'Existing Document',
        status: 'done',
        url: url,
      }];
    }

    form.setFieldsValue({
      business_name: record.business_name,
      email_id: record.email_id,
      mobile: record.mobile,
      document_name: record.document_name,
      document_type: record.document_type,
      document: existingFileList,
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setUpdating(true);
    try {
      const formData = new FormData();

      if (!isEditMode) {
        values.status = 'pending';
      }

      Object.keys(values).forEach(key => {
        if (key === 'document' && values[key]) {
          const fileList = values[key];
          if (fileList.length > 0) {
            const file = fileList[0];
            if (file.originFileObj) {
              formData.append('document', file.originFileObj);
            } else if (editingRecord && editingRecord.document) {
              formData.append('document', editingRecord.document);
            }
          }
        } else if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      const url = isEditMode
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/sow-documents/${editingRecord.id}/update/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/sow-documents/create/`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        body: formData,
      });
      const data = await response.json();
      if (data.success) {
        message.success(`Document ${isEditMode ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        fetchDocuments(); // Refresh table

        // Handle Fact-Ops redirect if present
        if (data.data?.fact_ops_response?.redirect_url) {
          window.open(data.data.fact_ops_response.redirect_url, '_blank');
        }
      } else {
        message.error(data.message || `Failed to ${isEditMode ? 'update' : 'create'} document`);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} document:`, error);
      message.error(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the document`);
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      title: 'Business Name',
      dataIndex: 'business_name',
      key: 'business_name',
    },
    {
      title: 'Email',
      dataIndex: 'email_id',
      key: 'email_id',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: 'Document Name',
      dataIndex: 'document_name',
      key: 'document_name',
    },
    {
      title: 'Type',
      dataIndex: 'document_type',
      key: 'document_type',
      render: (type) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = status?.toLowerCase() === 'pending' ? 'orange' : 'green';
        if (status?.toLowerCase() === 'rejected') color = 'red';
        return <Tag color={color}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => formatIndianDateTime(date),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        let url = record.document;
        if (url && !url.startsWith('http')) {
          url = `${import.meta.env.VITE_API_BASE_URL}/media/${url}`;
        }
        return (
          <Space>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
              disabled={!url}
            >
              View
            </Button>
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            >
              Edit
            </Button>
          </Space>
        );
      },
    },
  ];

  const filteredDocuments = documents.filter(doc =>
    Object.values(doc).some(val =>
      val !== null && val !== undefined && String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div style={{ padding: '24px', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <Title level={2} style={{ color: '#2c3e50', margin: 0 }}>
          <ProjectOutlined style={{ marginRight: '12px', color: '#FF986C' }} />
          Statement of Work (SOW) Documents
        </Title>
        <Space size="middle" wrap>
          <Input
            placeholder="Search documents..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New Document
          </Button>
        </Space>
      </div>

      <Card bordered={false} style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '12px' }}>
        <Table
          columns={columns}
          dataSource={filteredDocuments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={isEditMode ? "Edit SOW Document" : "Add New SOW Document"}
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        confirmLoading={updating}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="business_name" label="Business Name" rules={!isEditMode ? [{ required: true }] : []}>
                <Input placeholder="Enter business name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email_id" label="Email" rules={!isEditMode ? [{ required: true, type: 'email' }] : []}>
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="mobile" label="Mobile" rules={!isEditMode ? [{ required: true }] : []}>
                <Input placeholder="Enter mobile number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="document_name" label="Document Name" rules={!isEditMode ? [{ required: true }] : []}>
                <Input placeholder="Enter document name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="document_type" label="Document Type" rules={!isEditMode ? [{ required: true }] : []}>
                <Select placeholder="Select document type">
                  <Option value="Master Service Agreement (MSA)">Master Service Agreement (MSA)</Option>
                  <Option value="Statement of Work (SOW)">Statement of Work (SOW)</Option>
                  <Option value="Commercial Annexure">Commercial Annexure</Option>
                  <Option value="Service Level Agreement (SLA)">Service Level Agreement (SLA)</Option>
                  <Option value="Payment Settlement Policy">Payment Settlement Policy</Option>
                  <Option value="Platform Terms & Conditions">Platform Terms & Conditions</Option>
                  <Option value="Privacy & Data Processing Agreement">Privacy & Data Processing Agreement</Option>
                  <Option value="Category-Specific Compliance Documents">Category-Specific Compliance Documents</Option>
                  <Option value="Internal Operational Checklists & Onboarding Documents">Internal Operational Checklists & Onboarding Documents</Option>
                  <Option value="other documents">other documents</Option>
                </Select>
              </Form.Item>
            </Col>
            {!isEditMode && (
              <Col span={12}>
                <Form.Item
                  name="document"
                  label="Document File"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  rules={[{ required: true, message: 'Please upload a document' }]}
                >
                  <Upload beforeUpload={() => false} maxCount={1} listType="picture">
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Document Details"
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        {viewingRecord && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Document:</strong>{' '}
              <a
                href={viewingRecord.document && !viewingRecord.document.startsWith('http') ? `${import.meta.env.VITE_API_BASE_URL}/media/${viewingRecord.document}` : viewingRecord.document}
                target="_blank"
                rel="noopener noreferrer"
              >
                {viewingRecord.document_name || 'View Document'}
              </a>
            </div>

            <div style={{ marginBottom: '8px' }}>
              <strong>Signers:</strong>
            </div>
            {(() => {
              try {
                const signers = typeof viewingRecord.signers === 'string' ? JSON.parse(viewingRecord.signers) : viewingRecord.signers;
                if (!signers || !signers.length) {
                  return <p style={{ color: '#888' }}>No signers data available.</p>;
                }
                return (
                  <Table
                    dataSource={signers}
                    rowKey={(r, i) => i}
                    pagination={false}
                    size="small"
                    columns={[
                      { title: 'Name', dataIndex: 'name', key: 'name' },
                      { title: 'Email', dataIndex: 'email', key: 'email' },
                      { title: 'Signed At', dataIndex: 'signed_at', key: 'signed_at', render: (d) => d ? formatIndianDateTime(d) : 'Pending' }
                    ]}
                  />
                );
              } catch (e) {
                return <p style={{ color: 'red' }}>Invalid signers data.</p>;
              }
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};
export default SOWDocumentation;
